import { isDefaultOrganizationId } from '@/lib/auth/org-access';
import { useAuthStore } from '@/state/store/auth';

const ORG_SELECTION_REQUIRED_KEY = 'require-org-selection';
const PENDING_SIGNUP_ORG_ID_KEY = 'pending-signup-organization-id';
const SELECTED_ORG_ID_KEY = 'selected-org-id';

/** Mark that the next authenticated session must complete org selection (self-signup). */
export const markOrgSelectionRequired = (): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(ORG_SELECTION_REQUIRED_KEY, '1');
};

export const clearOrgSelectionRequired = (): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(ORG_SELECTION_REQUIRED_KEY);
};

export const isOrgSelectionRequired = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(ORG_SELECTION_REQUIRED_KEY) === '1';
};

/** Org chosen during signup — applied on activate and after first login. */
export const setPendingSignupOrganizationId = (organizationId: string): void => {
  if (typeof window === 'undefined') return;
  const id = organizationId.trim();
  if (!id) {
    window.sessionStorage.removeItem(PENDING_SIGNUP_ORG_ID_KEY);
    return;
  }
  window.sessionStorage.setItem(PENDING_SIGNUP_ORG_ID_KEY, id);
  window.localStorage.setItem(SELECTED_ORG_ID_KEY, id);
};

export const getPendingSignupOrganizationId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(PENDING_SIGNUP_ORG_ID_KEY)?.trim() || null;
};

export const clearPendingSignupOrganizationId = (): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_SIGNUP_ORG_ID_KEY);
};

/** Persist active org for subsequent auth grants (e.g. SSO org_id). */
export const persistSelectedOrganizationId = (organizationId: string): void => {
  if (typeof window === 'undefined') return;
  const id = organizationId.trim();
  if (!id) return;
  window.localStorage.setItem(SELECTED_ORG_ID_KEY, id);
};

/** Default authenticated landing path after login/signup. */
export const POST_AUTH_PATH = '/contacts';

/**
 * Post-auth destination after login/signup.
 * Skip org selection when the session is already on a non-Default org.
 */
export const getPostAuthPath = (): string => {
  if (!isOrgSelectionRequired()) return POST_AUTH_PATH;

  const selectedOrgId = useAuthStore.getState().selectedOrgId;
  if (selectedOrgId && !isDefaultOrganizationId(selectedOrgId)) {
    clearOrgSelectionRequired();
    return POST_AUTH_PATH;
  }

  return '/select-organization';
};
